import axiosInstance from "../axiosConfig";
import { logger } from "utils/logger";
import {
  APIResponse,
  CreateStudentPayload,
  CreateStudentResponse,
  PaginatedResponse,
  StudentActivityChannel,
  StudentCourseActionLog,
  StudentDocumentStatus,
  StudentPurchase,
  StudentCourseOrder,
} from "./studentService.types";

export interface StudentListFilters {
  studentIds?: string[];
  courseIds?: string[];
}

export const fetchStudents = async (
  page: number = 1,
  pageSize: number = 7,
  ordering: string = "",
  filters: StudentListFilters = {},
): Promise<APIResponse<StudentPurchase>> => {
  try {
    const response = await axiosInstance.get(`/courses/teacher/purchases/`, {
      params: {
        page,
        page_size: pageSize,
        ordering,
        student_ids: filters.studentIds?.length ? filters.studentIds.join(",") : undefined,
        course_ids: filters.courseIds?.length ? filters.courseIds.join(",") : undefined,
      },
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении списка учеников:", error);
    throw error;
  }
};

export const updateNote = async (id: number, note: string): Promise<void> => {
  try {
    await axiosInstance.patch(`courses/teacher/purchases/update_note/${id}/`, { note });
  } catch (error) {
    logger.error("Ошибка при обновлении заметки:", error);
    throw error;
  }
};

export const updateAccess = async (
  id: number,
  courseAccess: boolean,
): Promise<{ message: string; course_access: boolean }> => {
  try {
    const response = await axiosInstance.patch(`courses/teacher/purchases/update_access/${id}/`, {
      course_access: courseAccess,
    });
    return response.data;
  } catch (error) {
    logger.error("Ошибка при обновлении доступа:", error);
    throw error;
  }
};

export const fetchPurchaseById = async (purchaseId: number): Promise<StudentPurchase> => {
  try {
    const response = await axiosInstance.get<StudentPurchase>(`courses/teacher/purchases/${purchaseId}/`);
    return response.data;
  } catch (error) {
    logger.error(`Ошибка при получении данных о покупке с ID ${purchaseId}:`, error);
    throw error;
  }
};

export const createStudent = async (payload: CreateStudentPayload): Promise<CreateStudentResponse> => {
  try {
    const response = await axiosInstance.post<CreateStudentResponse>(
      `courses/teacher/purchases/create_student/`,
      payload,
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при создании студента:", error);
    throw error;
  }
};

interface FetchStudentLogsParams {
  studentId: number | string;
  courseId: number | string;
  page?: number;
  pageSize?: number;
  ordering?: string;
  lessonNumber?: number | string;
  assignmentTitle?: string;
  channel?: StudentActivityChannel | "";
}

export const fetchStudentLogs = async ({
  studentId,
  courseId,
  page = 1,
  pageSize = 10,
  ordering = "-created",
  lessonNumber,
  assignmentTitle,
  channel,
}: FetchStudentLogsParams): Promise<PaginatedResponse<StudentCourseActionLog>> => {
  try {
    const response = await axiosInstance.get<PaginatedResponse<StudentCourseActionLog>>(
      `student/student-logs/`,
      {
        params: {
          student_id: studentId,
          course_id: courseId,
          page,
          page_size: pageSize,
          ordering,
          lesson_number: lessonNumber,
          assignment_title: assignmentTitle,
          channel: channel || undefined,
        },
      },
    );
    return response.data;
  } catch (error) {
    logger.error("Ошибка при получении логов ученика:", error);
    throw error;
  }
};

interface DownloadStudentLogsParams {
  studentId: number | string;
  courseId: number | string;
  ordering?: string;
  lessonNumber?: number | string;
  assignmentTitle?: string;
  channel?: StudentActivityChannel | "";
}

export const downloadStudentLogsXlsx = async ({
  studentId,
  courseId,
  ordering,
  lessonNumber,
  assignmentTitle,
  channel,
}: DownloadStudentLogsParams): Promise<Blob> => {
  try {
    const response = await axiosInstance.get(`student/student-logs/export/`, {
      params: {
        student_id: studentId,
        course_id: courseId,
        ordering,
        lesson_number: lessonNumber,
        assignment_title: assignmentTitle,
        channel: channel || undefined,
      },
      responseType: "blob",
    });
    return response.data as Blob;
  } catch (error) {
    logger.error("Ошибка при скачивании логов ученика:", error);
    throw error;
  }
};

export const fetchStudentDocumentStatus = async (
  studentId: number,
  courseId: number,
): Promise<StudentDocumentStatus> => {
  const response = await axiosInstance.get<StudentDocumentStatus>(
    `courses/teacher/students/${studentId}/courses/${courseId}/document/`,
  );
  return response.data;
};

export const updateStudentDocumentStatus = async (
  studentId: number,
  courseId: number,
  payload: Pick<StudentDocumentStatus, "document_type" | "status"> & { comment?: string | null },
): Promise<StudentDocumentStatus> => {
  const response = await axiosInstance.put<StudentDocumentStatus>(
    `courses/teacher/students/${studentId}/courses/${courseId}/document/`,
    payload,
  );
  return response.data;
};

export const fetchStudentCourseOrders = async (paymentId: number): Promise<StudentCourseOrder[]> => {
  const response = await axiosInstance.get<StudentCourseOrder[]>(`courses/teacher/purchases/${paymentId}/orders/`);
  return response.data;
};

export const downloadStudentCourseOrder = async (order: Pick<StudentCourseOrder, "id" | "kind">): Promise<void> => {
  const response = await axiosInstance.get(`courses/teacher/orders/${order.id}/download/`, {
    responseType: "blob",
  });
  const objectUrl = URL.createObjectURL(response.data as Blob);
  const anchor = document.createElement("a");
  anchor.href = objectUrl;
  anchor.download = `prikaz_${order.id}_${order.kind}.docx`;
  anchor.style.display = "none";
  document.body.appendChild(anchor);
  try {
    anchor.click();
  } finally {
    anchor.remove();
    URL.revokeObjectURL(objectUrl);
  }
};

export const earlyExpelStudent = async (paymentId: number): Promise<{
  payment_id: number;
  course_access: boolean;
  ended_at: string;
  end_reason: "early_expulsion";
}> => {
  const response = await axiosInstance.post(`courses/teacher/purchases/${paymentId}/expel/`);
  return response.data;
};